import {
  createFeedbackCapture,
  type FeedbackCaptureApi,
  type FeedbackCaptureFn,
} from "@mcblessing1/in-app-feedback";
import posthog from "posthog-js";
import { getProductFeedbackSurveyConfig } from "./productFeedbackSurveyConfig";
import { resolveProductFeedbackGate } from "./resolveProductFeedbackGate";

export type CreateProductFeedbackCaptureOptions = {
  /** Override pathname for tests / SSR. */
  getPathname?: () => string;
  /** Override capture for tests. */
  capture?: FeedbackCaptureFn;
  /** Override app version property. */
  getAppVersion?: () => string;
};

function defaultCapture(
  event: string,
  properties: Record<string, unknown>,
): void {
  try {
    posthog.capture(event, properties);
  } catch (error) {
    console.warn("[product-feedback] capture failed:", error);
  }
}

function defaultPathname(): string {
  if (typeof window !== "undefined") return window.location.pathname || "/";
  return "/";
}

/**
 * Single product-feedback capture owner — UI must call this API only.
 * Gate uses env ∧ Admin Settings (`isProductFeedbackMasterEnabled`); no end-user opt-out.
 */
export function createProductFeedbackCapture(
  options: CreateProductFeedbackCaptureOptions = {},
): FeedbackCaptureApi {
  const getPathname = options.getPathname ?? defaultPathname;
  const capture = options.capture ?? defaultCapture;
  const getAppVersion = options.getAppVersion ?? (() => "0.1.0");

  return createFeedbackCapture({
    getGate: () => resolveProductFeedbackGate({ pathname: getPathname() }),
    getSurveyConfig: getProductFeedbackSurveyConfig,
    capture,
    resolvePath: getPathname,
    resolveAppVersion: getAppVersion,
  });
}

let singleton: FeedbackCaptureApi | null = null;

/** Lazy singleton for host UI (Phase 2+). Tests should call `createProductFeedbackCapture`. */
export function getProductFeedbackCapture(): FeedbackCaptureApi {
  if (!singleton) {
    singleton = createProductFeedbackCapture();
  }
  return singleton;
}

/** Test helper — clears singleton between cases. */
export function resetProductFeedbackCaptureForTests(): void {
  singleton = null;
}
